'use client'

import { useEffect, useMemo, useRef } from 'react'
import { TableOfContents as TableOfContentsIcon } from 'lucide-react'
import { RightEdgePanel } from '@/components/ui/right-edge-panel'
import { useActiveHeading } from './useActiveHeading'
import type { TOCHeading } from './TableOfContents'

interface Props {
  headings: TOCHeading[]
}

export function MobileTableOfContents({ headings }: Props) {
  // Filter to only h2 headings for mobile
  const h2Headings = useMemo(() => headings.filter((h) => h.level <= 2), [headings])

  const { activeId, pin } = useActiveHeading(h2Headings)

  if (h2Headings.length === 0) return null

  return (
    <RightEdgePanel
      className="lg:hidden"
      toggleIcon={<TableOfContentsIcon className="size-6" />}
      ariaLabel="Toggle table of contents"
      panelClassName="flex flex-col gap-2.5 p-4 text-white max-w-[303px]"
    >
      {h2Headings.map((heading) => (
        <a
          key={heading.id}
          href={`#${heading.id}`}
          onClick={(e) => {
            e.preventDefault()
            const el = document.getElementById(heading.id)
            if (el) {
              // An entry can point at a collapsed disclosure, which scrolls to an empty row.
              const details = el.closest('details') as HTMLDetailsElement | null
              if (details) details.open = true
              pin(heading.id)
              el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          }}
          data-toc-mobile-id={heading.id}
          className={`block subheading-xsmall leading-6 transition-colors ${
            activeId === heading.id ? 'text-white' : 'text-white/60 hover:text-white'
          }`}
        >
          {heading.text}
        </a>
      ))}

      {/* Active indicator - civo-lime bar on the right edge */}
      <ActiveIndicator activeId={activeId} />
    </RightEdgePanel>
  )
}

function ActiveIndicator({ activeId }: { activeId: string }) {
  const barRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const bar = barRef.current
    if (!bar) return

    const panel = bar.parentElement
    if (!panel || !activeId) {
      bar.style.opacity = '0'
      return
    }

    const link = panel.querySelector(`[data-toc-mobile-id="${activeId}"]`) as HTMLElement | null
    if (!link) {
      bar.style.opacity = '0'
      return
    }

    const panelRect = panel.getBoundingClientRect()
    const linkRect = link.getBoundingClientRect()

    // + scrollTop: the panel scrolls, and an absolute child is offset from the unscrolled origin.
    bar.style.top = `${linkRect.top - panelRect.top + panel.scrollTop}px`
    bar.style.height = `${linkRect.height}px`
    bar.style.opacity = '1'
  }, [activeId])

  return (
    <div
      ref={barRef}
      className="absolute right-0 w-1 bg-civo-lime transition-[top,height,opacity] duration-200 ease-out"
      style={{ opacity: 0 }}
    />
  )
}
